import { NgClass } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { type Device, DeviceStatus, DeviceUptimeStatus } from '@api/devices';

import { SystemDeviceTypesIds } from '../../models/device-types';

export const DeviceDisplayStatus = {
  ...DeviceUptimeStatus,
  ARCHIVED: DeviceStatus.ARCHIVED,
} as const;

export type DeviceDisplayStatus = (typeof DeviceDisplayStatus)[keyof typeof DeviceDisplayStatus];

const DEVICE_OFFLINE_TIMEOUT_SECONDS = 180;
const CAMERA_OFFLINE_TIMEOUT_SECONDS = 86_460;

type DisplayStatusSource = Partial<Pick<Device, 'deviceTypeId'>> & Pick<Device, 'latestStreamingTimestamp' | 'status' | 'uptimeStatus'>;

export function computeDeviceDisplayStatus(device: DisplayStatusSource) {
  if (device.status === DeviceStatus.PAUSED) {
    return DeviceDisplayStatus.PAUSED;
  }

  if (device.status === DeviceStatus.ARCHIVED) {
    return DeviceDisplayStatus.ARCHIVED;
  }

  if (device.uptimeStatus === DeviceUptimeStatus.ONLINE) {
    return DeviceDisplayStatus.ONLINE;
  }

  if (device.uptimeStatus === DeviceUptimeStatus.OFFLINE) {
    return DeviceDisplayStatus.OFFLINE;
  }

  if (!device.latestStreamingTimestamp) {
    return DeviceDisplayStatus.OFFLINE;
  }

  const timeoutSeconds = device.deviceTypeId === SystemDeviceTypesIds.Camera
    ? CAMERA_OFFLINE_TIMEOUT_SECONDS
    : DEVICE_OFFLINE_TIMEOUT_SECONDS;
  const staleSeconds = (Date.now() - new Date(device.latestStreamingTimestamp).getTime()) / 1000;

  return staleSeconds >= timeoutSeconds ? DeviceDisplayStatus.OFFLINE : DeviceDisplayStatus.ONLINE;
}

const LABEL: Record<DeviceDisplayStatus, string> = {
  [DeviceDisplayStatus.ARCHIVED]: 'Archived',
  [DeviceDisplayStatus.OFFLINE]: 'Offline',
  [DeviceDisplayStatus.ONLINE]: 'Online',
  [DeviceDisplayStatus.PAUSED]: 'Paused',
};

@Component({
  imports: [NgClass],
  selector: 'avs-device-display-status',
  standalone: true,
  styleUrl: './device-display-status.component.scss',
  template: `<span
    class="status-block"
    [ngClass]="statusClass()"
    >{{ label() }}</span
  >`,
})
export class DeviceDisplayStatusComponent {
  device = input.required<Device>();

  protected status = computed(() => computeDeviceDisplayStatus(this.device()));
  protected label = computed(() => LABEL[this.status()]);
  protected statusClass = computed(() => `status-${this.status()}`);
}
